import { ChangeEvent, RefObject } from "react";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import {
  PATROCINADOR_LOGO_HEIGHT,
  PATROCINADOR_LOGO_WIDTH,
} from "helpers/validateImageDimensions";
import { IPatrocinador } from "interfaces/patrocinador";
import {
  fieldSx,
  modalHeaderSx,
  modalPaperSx,
  PATROCINADORES_ACCENT,
  saveButtonSx,
} from "../patrocinadores.theme";

type EventoOption = { id: number; titulo: string };

type FormState = {
  nome: string;
  descricao: string;
  urlSite: string;
  ordem: number;
  ativo: boolean;
  exibirHome: boolean;
};

interface Props {
  open: boolean;
  selected: IPatrocinador | null;
  form: FormState;
  logoPreview: string;
  fileInputRef: RefObject<HTMLInputElement>;
  eventosAtivos: EventoOption[];
  selectedEventos: EventoOption[];
  loadingEventos: boolean;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onFormChange: (patch: Partial<FormState>) => void;
  onLogoChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onSelectedEventosChange: (eventos: EventoOption[]) => void;
}

export default function PatrocinadorFormDialog({
  open,
  selected,
  form,
  logoPreview,
  fileInputRef,
  eventosAtivos,
  selectedEventos,
  loadingEventos,
  isSubmitting,
  onClose,
  onSubmit,
  onFormChange,
  onLogoChange,
  onSelectedEventosChange,
}: Props): JSX.Element {
  const isEdit = Boolean(selected);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: modalPaperSx }}
    >
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" minWidth={0}>
            <HandshakeOutlinedIcon sx={{ fontSize: 40, opacity: 0.9 }} />
            <Box minWidth={0}>
              <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.2 }}>
                {isEdit ? "Editar patrocinador" : "Novo patrocinador"}
              </Typography>
              <Typography variant="h5" fontWeight="bold" noWrap>
                {isEdit ? selected?.nome : "Cadastro de patrocinador"}
              </Typography>
              {isEdit ? (
                <Box mt={0.75}>
                  <Chip
                    size="small"
                    label={selected!.ativo ? "Ativo" : "Inativo"}
                    sx={{
                      bgcolor: selected!.ativo
                        ? "rgba(16, 185, 129, 0.9)"
                        : "rgba(255, 255, 255, 0.15)",
                      color: "#fff",
                      fontWeight: 600,
                    }}
                  />
                </Box>
              ) : null}
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: "inherit" }} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <MDBox sx={{ p: 3, overflowY: "auto", flex: 1 }}>
        <Grid container spacing={2.5}>
          <Grid item xs={12} display="flex" flexDirection="column" alignItems="center" gap={1}>
            <Avatar
              src={logoPreview}
              alt="Logo preview"
              variant="rounded"
              sx={{
                width: 120,
                height: 68,
                cursor: "pointer",
                border: "2px dashed",
                borderColor: "grey.300",
                bgcolor: "grey.50",
              }}
              onClick={() => fileInputRef.current?.click()}
            />
            <Typography variant="caption" color="text.secondary" textAlign="center">
              Clique para selecionar o logo ({PATROCINADOR_LOGO_WIDTH} × {PATROCINADOR_LOGO_HEIGHT}{" "}
              px, proporção 16:9)
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={onLogoChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Nome *"
              fullWidth
              size="small"
              value={form.nome}
              onChange={(e) => onFormChange({ nome: e.target.value })}
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Breve descrição"
              fullWidth
              size="small"
              multiline
              minRows={3}
              value={form.descricao}
              onChange={(e) => onFormChange({ descricao: e.target.value.slice(0, 60) })}
              placeholder="Ex.: Loja esportiva parceira da prova..."
              helperText={`${form.descricao.length}/60 caracteres`}
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="URL do site"
              fullWidth
              size="small"
              value={form.urlSite}
              onChange={(e) => onFormChange({ urlSite: e.target.value })}
              placeholder="https://..."
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={eventosAtivos}
              getOptionLabel={(o) => o.titulo}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={selectedEventos}
              onChange={(_, newValue) => onSelectedEventosChange(newValue)}
              loading={loadingEventos}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vincular a eventos"
                  size="small"
                  placeholder="Selecione eventos..."
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingEventos ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.titulo}
                    size="small"
                  />
                ))
              }
              noOptionsText="Nenhum evento encontrado"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Ordem"
              type="number"
              fullWidth
              size="small"
              value={form.ordem}
              onChange={(e) => onFormChange({ ordem: Number(e.target.value) })}
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} display="flex" alignItems="center" flexWrap="wrap" gap={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.ativo}
                  onChange={(e) => onFormChange({ ativo: e.target.checked })}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: PATROCINADORES_ACCENT },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      bgcolor: PATROCINADORES_ACCENT,
                    },
                  }}
                />
              }
              label="Ativo"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.exibirHome}
                  onChange={(e) => onFormChange({ exibirHome: e.target.checked })}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: PATROCINADORES_ACCENT },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      bgcolor: PATROCINADORES_ACCENT,
                    },
                  }}
                />
              }
              label="Exibir na Home"
            />
          </Grid>

          <Grid item xs={12}>
            <Alert severity="info" sx={{ py: 0.5, fontSize: "0.75rem", borderRadius: 1.5 }}>
              Regras de exibição: com <strong>Exibir na Home</strong> ativo e evento selecionado,
              exibe em <strong>Home + Evento</strong>; sem Home e com evento, exibe em{" "}
              <strong>Somente Evento</strong>; com Home e sem evento, exibe em{" "}
              <strong>Somente Home</strong>.
            </Alert>
          </Grid>
        </Grid>
      </MDBox>

      <MDBox
        px={3}
        py={2}
        display="flex"
        gap={1}
        justifyContent="flex-end"
        borderTop="1px solid"
        borderColor="grey.200"
      >
        <MDButton type="button" variant="outlined" color="dark" onClick={onClose} disabled={isSubmitting}>
          Cancelar
        </MDButton>
        <MDButton
          type="button"
          variant="gradient"
          color="dark"
          onClick={onSubmit}
          disabled={isSubmitting}
          sx={saveButtonSx}
        >
          {isSubmitting ? "Salvando…" : isEdit ? "Salvar alterações" : "Criar patrocinador"}
        </MDButton>
      </MDBox>
    </Dialog>
  );
}
